import { cn } from "cn";
import { Fragment, type ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { SeparatorDot } from "@/components/ui/separator";
import {
  ATTACHMENT_DRAG_SOURCE_CLASS_NAME,
  getAttachmentsDragSourceProps,
} from "@/libs/attachment-drag";
import type { AttachmentSource } from "@/libs/form-attachment";
import type { AttachmentUploader } from "./attachment";
import { AttachmentFileName } from "./attachment-file-name";
import { type AttachmentAction, AttachmentMenu } from "./attachment-menu";
import { AttachmentPicture } from "./attachment-picture";
import type { AttachmentFile } from "./attachment-preview-kind";
import { attachmentStateBackground } from "./attachment-state-background";
import { AttachmentUploaderAvatar } from "./attachment-uploader-avatar";

export type AttachmentRowProps = AttachmentFile & {
  /**
   * The line under the name, one value per entry — where the file belongs, its
   * date, its size. Empty entries (`null`, `false`, `""`) are skipped and the
   * row puts the dots between the rest, so a missing value never leaves one.
   */
  meta?: ReactNode[];
  /**
   * Trailing meta entries that matter less — a size, a label. They show, dots
   * and all, only once the row is wide enough, so a narrow row never clips one
   * behind a dangling dot.
   */
  metaDetail?: ReactNode[];
  /** The leading checkbox renders when `onSelectedChange` is passed. */
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  /** The ringed avatar on the picture's corner. */
  uploader?: AttachmentUploader;
  /** Opens the file — a lightbox, typically. The picture and the name are its targets. */
  onClick?: () => void;
  /** Present ⇒ the picture (not the name) can be dragged into a composer, carrying these files. */
  dragAttachments?: AttachmentSource[];
  /** Names the row's test hooks: `<prefix>-row` and `<prefix>-select`. */
  dataTestPrefix?: string;
  className?: string;
} & (
    | {
        /** The "…" menu on the right. */
        actions?: AttachmentAction[];
        menu?: never;
      }
    | {
        /** A caller's own "…" menu, in place of `actions`; it reveals on `group-hover/attachment`. */
        menu: ReactNode;
        actions?: never;
      }
  );

/**
 * One attachment as a list row (design `ItemListFile` 14559:119204) — the list
 * counterpart of the `Attachment` tile: a 40px picture (an image or a video's
 * poster) or the file's type icon, the uploader's avatar on its corner, the
 * name with its extension kept readable, a meta line under it, and the "…"
 * menu on the right. Selectable rows lead with a checkbox, always shown — a
 * list has no hover-only affordances to discover. Hovered or selected, the row
 * takes the tile caption's tints over its white fill.
 */
/** A meta entry with something to show — not `null`, `undefined`, `false` or `""`. */
function hasContent(entry: ReactNode): boolean {
  return entry != null && entry !== false && entry !== "";
}

export function AttachmentRow({
  fileName,
  originalContentUri,
  thumbnailUri,
  contentType,
  meta,
  metaDetail,
  selected = false,
  onSelectedChange,
  actions,
  menu,
  uploader,
  onClick,
  dragAttachments,
  dataTestPrefix = "attachment",
  className,
}: AttachmentRowProps) {
  const metaEntries = meta?.filter(hasContent) ?? [];
  const detailEntries = metaDetail?.filter(hasContent) ?? [];
  return (
    <div
      data-test={`${dataTestPrefix}-row`}
      className={cn(
        /* sorted */
        "flex",
        "items-center gap-3",
        "p-2",
        "bg-bg-light border border-border-dark-low rounded-2xl",
        "transition-colors",
        "group/attachment",
        attachmentStateBackground(selected, "self"),
        className,
      )}
    >
      {onSelectedChange && (
        <Checkbox
          checked={selected}
          aria-label={`Select ${fileName}`}
          data-test={`${dataTestPrefix}-select`}
          onClick={(event) => event.stopPropagation()}
          onCheckedChange={(checked) => onSelectedChange(checked === true)}
          className="ml-2"
        />
      )}
      {/* A pointer target only — the name button opens the file from the keyboard. Not a <button>, so it can start a drag in Firefox; its padding holds the avatar's 4px overhang (design `Col / Main`) inside the drag ghost; -mr-1 keeps the picture 12px from the name. A flex box, so an image's inline-block picture drops its baseline gap and centres like the type icon. */}
      <div
        onClick={onClick}
        {...(dragAttachments && getAttachmentsDragSourceProps(dragAttachments))}
        className={cn(
          /* sorted */
          "relative flex shrink-0",
          "p-1",
          dragAttachments
            ? ATTACHMENT_DRAG_SOURCE_CLASS_NAME
            : onClick && "cursor-pointer",
        )}
      >
        <AttachmentPicture
          file={{ fileName, originalContentUri, thumbnailUri, contentType }}
        />
        {uploader && (
          <AttachmentUploaderAvatar
            uploader={uploader}
            className="absolute right-0 bottom-0"
          />
        )}
      </div>

      <div className="flex flex-col grow min-w-0 @container/attachment-row">
        <button
          type="button"
          onClick={onClick}
          disabled={!onClick}
          className={cn(
            /* sorted */
            "flex",
            "min-w-0",
            "text-left",
            "cursor-pointer",
            "disabled:cursor-default",
          )}
        >
          <AttachmentFileName fileName={fileName} />
        </button>
        {metaEntries.length + detailEntries.length > 0 && (
          // One line that never wraps: fixed values keep their width, the rest truncates.
          <div
            className={cn(
              /* sorted */
              "flex",
              "items-center gap-2",
              "min-w-0",
              "overflow-hidden",
              "text-sm text-text-dark-high whitespace-nowrap",
            )}
          >
            <MetaEntries entries={metaEntries} />
            {detailEntries.length > 0 && (
              <span
                className={cn(
                  /* sorted */
                  "hidden",
                  "items-center gap-2",
                  "min-w-0",
                  "@md/attachment-row:flex",
                )}
              >
                {metaEntries.length > 0 && <SeparatorDot />}
                <MetaEntries entries={detailEntries} />
              </span>
            )}
          </div>
        )}
      </div>

      {menu ??
        (actions && actions.length > 0 && (
          <AttachmentMenu
            fileName={fileName}
            actions={actions}
            className="shrink-0"
          />
        ))}
    </div>
  );
}

/** Meta values with a dot between each two. */
function MetaEntries({ entries }: { entries: ReactNode[] }) {
  return entries.map((entry, index) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: meta entries are positional and never reorder within a row
    <Fragment key={index}>
      {index > 0 && <SeparatorDot />}
      {entry}
    </Fragment>
  ));
}
